import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const LearnSingleSlug = () => {
  const { slug1, slug2, slug3, slug4 } = useParams();

  const [categoryName, setCategoryName] = useState("");
  const [categoryDescription, setCategoryDescription] = useState("");
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedItems, setExpandedItems] = useState(new Set());

  useEffect(() => {
    const fetchSubcategories = async () => {
      try {
        // Build params dynamically based on number of slugs
        let params = {};
        if (slug1) params.parent = slug1;
        if (slug2) params.child = slug2;
        if (slug3) params.grandchild = slug3;
        if (slug4) params.greatgrandchild = slug4;

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/fetch-categories`,
          { params }
        );

        if (response.data?.data?.terms?.length > 0) {
          const categoryData = response.data.data.terms[0];
          setCategoryName(categoryData.name);
          setCategoryDescription(categoryData.description);
          setSubcategories(categoryData.children || []);
        } else {
          setSubcategories([]);
        }
      } catch (error) {
        console.error("Error fetching subcategories:", error);
        setSubcategories([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSubcategories();
  }, [slug1, slug2, slug3, slug4]);

  const buildPath = (...slugs) => `/learn/${slugs.filter(Boolean).join("/")}`;

  const toggleExpand = (id) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  };

  const renderChildren = (children, parentSlugs = [], level = 0) => (
    <ul className="list-group list-group-flush ms-4">
      {children.map((child) => {
        const currentSlugs = [...parentSlugs, child.slug];
        const hasChildren = child.children?.length > 0;

        return (
          <li key={child.id} className="list-group-item border-0 ps-0 py-2">
            <div className="d-flex align-items-center">
              {hasChildren ? (
                <button
                  className="btn btn-sm btn-outline-secondary me-2"
                  onClick={() => toggleExpand(child.id)}
                  style={{ width: "28px", height: "28px" }}
                >
                  <i
                    className={`bi ${
                      expandedItems.has(child.id)
                        ? "bi-chevron-down"
                        : "bi-chevron-right"
                    }`}
                  ></i>
                </button>
              ) : (
                <div className="me-2" style={{ width: "28px", height: "28px" }}>
                  <i className="bi bi-file-text text-muted"></i>
                </div>
              )}
              {child.taxonomy ? (
                <a
                  href={buildPath(...currentSlugs)}
                  className="text-decoration-none text-dark fw-medium flex-grow-1"
                >
                  {child.name}
                </a>
              ) : (
                <span className="text-dark fw-medium flex-grow-1">
                  {child.name}
                </span>
              )}
            </div>

            {hasChildren && expandedItems.has(child.id) && (
              <div className="mt-2">
                {renderChildren(child.children, currentSlugs, level + 1)}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );

  if (loading) {
    return (
      <div className="container py-4">
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!subcategories || subcategories.length === 0) {
    return (
      <div className="container py-4">
        <div className="card shadow-sm border-0">
          <div className="card-body text-center py-5">
            <i className="bi bi-folder-x display-4 text-muted mb-3"></i>
            <h5 className="text-muted">No Content Available</h5>
            <p className="text-muted">
              This category doesn't have any subcategories or content yet.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card shadow-sm border-0">
        <div className="card-header bg-white">
          <div className="d-flex align-items-center mb-2">
            <i className="bi bi-journals text-primary me-3 fs-2"></i>
            <div>
              <h1 className="h3 mb-1 text-dark">{categoryName}</h1>
              {categoryDescription && (
                <p className="text-muted mb-0">{categoryDescription}</p>
              )}
            </div>
          </div>
        </div>

        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Subcategories & Content</h5>
            <span className="badge bg-primary">
              {subcategories.length} item
              {subcategories.length !== 1 ? "s" : ""}
            </span>
          </div>

          <div className="list-group">
            {subcategories.map((sub) => {
              const hasChildren = sub.children?.length > 0;

              return (
                <div
                  key={sub.id}
                  className="list-group-item border-0 p-3 mb-2 rounded shadow-sm"
                >
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      <div
                        className={`rounded-circle d-flex align-items-center justify-content-center me-3 ${
                          sub.taxonomy ? "bg-primary" : "bg-secondary"
                        }`}
                        style={{ width: "40px", height: "40px" }}
                      >
                        <i
                          className={`bi ${
                            sub.taxonomy ? "bi-folder" : "bi-file-text"
                          } text-white`}
                        ></i>
                      </div>
                      <div>
                        {sub.taxonomy ? (
                          <a
                            href={buildPath(
                              slug1,
                              slug2,
                              slug3,
                              slug4,
                              sub.slug
                            )}
                            className="text-decoration-none text-dark fw-semibold"
                          >
                            {sub.name}
                          </a>
                        ) : (
                          <span className="text-dark fw-semibold">
                            {sub.name}
                          </span>
                        )}
                        {sub.description && (
                          <p className="text-muted small mb-0">
                            {sub.description}
                          </p>
                        )}
                      </div>
                    </div>

                    {hasChildren && (
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => toggleExpand(sub.id)}
                      >
                        <i
                          className={`bi ${
                            expandedItems.has(sub.id)
                              ? "bi-chevron-up"
                              : "bi-chevron-down"
                          } me-1`}
                        ></i>
                        {expandedItems.has(sub.id) ? "Hide" : "Show"} Content
                      </button>
                    )}
                  </div>

                  {hasChildren && expandedItems.has(sub.id) && (
                    <div className="mt-3 pt-3 border-top">
                      <h6 className="text-muted mb-2">Contents:</h6>
                      {renderChildren(sub.children, [
                        slug1,
                        slug2,
                        slug3,
                        slug4,
                        sub.slug,
                      ])}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LearnSingleSlug;
